import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PolarAngleAxis, PolarGrid, Radar, RadarChart, ResponsiveContainer } from "recharts";
import { RotateCcw, ShoppingBag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, euro } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { levelOf } from "@/lib/softskills";
import { ConsultForm } from "@/components/test/ConsultForm";

const SkillRow = ({ r, i }) => {
  const lvl = levelOf(r.score);
  return (
    <motion.div data-testid={`result-skill-${r.key}`} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.06 }}
      className="border-b border-white/5 py-3">
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="text-sm">{r.name}</span>
        <span className="font-mono text-xs" style={{ color: lvl.color }}>{lvl.label} · {r.score}</span>
      </div>
      <div className="h-1.5 bg-white/10">
        <motion.div className="h-full" style={{ background: lvl.color }} initial={{ width: 0 }} animate={{ width: `${r.score}%` }} transition={{ duration: 0.9, delay: 0.3 + i * 0.06 }} />
      </div>
    </motion.div>
  );
};

const GUIDES = {
  org: { prefix: "Organizzazione Settimanale", eyebrow: "Allena la tua organizzazione", title: "Organizzazione Settimanale", text: "Il manuale per dare un posto ad allenamento, pasti e recupero nella settimana, tra lavoro e impegni." },
};

const GuideCta = ({ showOrg }) => {
  const [products, setProducts] = useState([]);
  const { add, setOpen } = useCart();
  useEffect(() => {
    api.get("/products", { params: { category: "digitale" } })
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProducts([]));
  }, []);
  const keys = showOrg ? ["org"] : [];
  const items = keys.map((k) => ({ ...GUIDES[k], k, guide: products.find((p) => p.name?.startsWith(GUIDES[k].prefix)) })).filter((x) => x.guide);
  return <div className="space-y-4">{items.map((it) => <GuideCard key={it.k} it={it} add={add} setOpen={setOpen} />)}</div>;
};

const GuideCard = ({ it, add, setOpen }) => {
  const { guide } = it;
  return (
    <div data-testid={`result-guide-cta-${it.k}`} className="flex flex-col gap-5 bg-[#98bfa2] p-6 text-[#050807] sm:flex-row sm:items-center md:p-8">
      <img src={guide.image} alt="" className="h-28 w-28 shrink-0 object-cover" />
      <div className="flex-1">
        <p className="font-mono text-xs uppercase tracking-widest">{it.eyebrow}</p>
        <h3 className="mt-1 font-display text-3xl font-900 uppercase leading-none">{it.title}</h3>
        <p className="mt-2 text-sm text-[#050807]/80">{it.text}</p>
      </div>
      <div className="flex gap-2 sm:flex-col">
        <button data-testid={`result-guide-add-btn-${it.k}`} onClick={() => { add(guide); toast.success("Guida aggiunta al carrello"); setOpen(true); }}
          className="flex items-center justify-center gap-2 bg-[#050807] px-5 py-3 font-display text-lg font-800 uppercase text-[#f4f7f5] hover:bg-[#0b120e]">
          <ShoppingBag size={16} /> {euro(guide.price)}
        </button>
        <Link to={`/prodotto/${guide.id}`} data-testid="result-guide-link" className="px-5 py-3 text-center font-mono text-xs uppercase tracking-widest underline">Dettagli</Link>
      </div>
    </div>
  );
};

export const TestResult = ({ results, overall, onRestart, onClear }) => {
  const sorted = [...results].sort((a, b) => b.score - a.score);
  const strengths = sorted.slice(0, 2);
  const toTrain = sorted.slice(-2).reverse();
  const data = results.map((r) => ({ skill: r.short, score: r.score }));

  return (
    <div data-testid="test-result" className="mx-auto w-full max-w-[1200px] space-y-6">
      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <div className="border border-[rgba(152,191,162,0.2)] bg-[#0b120e] p-6 md:p-8">
          <p className="eyebrow">Il tuo profilo</p>
          <p className="mt-2 font-display text-7xl font-900 leading-none md:text-8xl"><span data-testid="result-overall">{overall}</span><span className="text-3xl text-[#a3b1aa]">/100</span></p>
          <div className="mt-2 h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={data} outerRadius="58%" margin={{ left: 36, right: 36 }}>
                <PolarGrid stroke="rgba(152,191,162,0.2)" />
                <PolarAngleAxis dataKey="skill" tick={{ fill: "#a3b1aa", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <Radar dataKey="score" stroke="#c4dcca" fill="#98bfa2" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-[rgba(152,191,162,0.2)] bg-[#0b120e] p-6 md:p-8">
          <p className="eyebrow mb-3">Le 9 competenze</p>
          {results.map((r, i) => <SkillRow key={r.key} r={r} i={i} />)}
        </div>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <div data-testid="result-strengths" className="border border-[rgba(152,191,162,0.2)] p-6 md:p-8">
          <p className="eyebrow mb-4">I tuoi punti di forza</p>
          {strengths.map((s) => (
            <div key={s.key} className="mb-5">
              <h3 className="font-display text-3xl font-900 uppercase leading-none">{s.name}</h3>
              <p className="mt-2 text-sm italic text-[#a3b1aa]">«{s.quote}»</p>
            </div>
          ))}
        </div>
        <div data-testid="result-to-train" className="border border-[rgba(240,167,143,0.35)] p-6 md:p-8">
          <p className="eyebrow mb-4 !text-[#f0a78f]">Da allenare per prime</p>
          {toTrain.map((s) => (
            <div key={s.key} className="mb-5">
              <h3 className="font-display text-3xl font-900 uppercase leading-none">{s.name}</h3>
              <p className="mt-2 text-sm text-[#a3b1aa]"><span className="text-[#c4dcca]">In pratica:</span> {s.tip}</p>
            </div>
          ))}
        </div>
      </div>
      <ConsultForm results={results} overall={overall} />
      <GuideCta showOrg={results.find((r) => r.key === "organizzazione")?.score < 75 || toTrain.some((s) => s.key === "organizzazione")} />
      <div className="flex flex-col items-center gap-4 pt-4 text-center">
        <div className="flex flex-wrap justify-center gap-6">
          <button data-testid="test-restart-btn" onClick={onRestart} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#a3b1aa] hover:text-[#c4dcca]">
            <RotateCcw size={14} /> Rifai il check-up
          </button>
          <button data-testid="test-clear-btn" onClick={onClear} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#a3b1aa] hover:text-[#f0a78f]">
            <Trash2 size={14} /> Cancella le mie risposte
          </button>
        </div>
        <p className="max-w-2xl text-xs leading-relaxed text-[#a3b1aa]">
          Check-up di autovalutazione a scopo educativo e informativo, ispirato ai temi della guida «Organizzazione Settimanale». Non è uno strumento validato né diagnostico e non sostituisce il parere di medici, psicologi o altri professionisti sanitari. I punteggi sono indicativi e dipendono dalla sincerità delle risposte. Le risposte restano solo su questo dispositivo: vengono inviate solo se scegli tu di mandarmi i risultati per la consulenza.
        </p>
      </div>
    </div>
  );
};
